import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import ActionMenu from "../../../../../components/app/ActionMenu";
import DeleteModal from "../../../../../components/app/DeleteModal";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Flex from "../../../../../components/Flex";
import { GlobalTableHeightWrapper } from "../../../../../components/GlobalTableHeightWrapper";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import Text from "../../../../../components/Text";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import useDisclosure, { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import ViewEmail from "../../../../../pages/app/provider/patient/patient-list/patient-chart-new/communication/ViewEmail";
import { usePatientEmailSend } from "../../../../../queries/patient/use-patient-email-send";
import { usePatientPortalEmailArchive } from "../../../../../queries/patient/use-patient-portal-email-archive";
import { usePatientPortalEmailMarkAsRead } from "../../../../../queries/patient/use-patient-portal-email-mark-as-read";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import {
  applyDefaultColumnSettings,
  DGT_Pagination,
  getSuccessSnackbar,
  PaginatedContent,
} from "../../../../../utils/utils";
import { COMMUNICATION_LAYOUT_HEIGHT } from "../PatientPortalCommunication";
import PatientSendBulkEmailModal from "./PatientSendBulkEmailModal";

// Type for the context passed from CommunicationLayout
interface CommunicationContext {
  sendMailDisclosure: DisclosureHost;
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: (model: GridRowSelectionModel) => void;
  pagination: DGT_Pagination;
  setPagination: (pagination: DGT_Pagination) => void;
  patientEmailList: PaginatedContent<any>;
  patientEmailListLoading: boolean;
  patientEmailListRefetching: boolean;
  refetchUnreadCount: () => void;
}

const PatientPortalEmailTable = () => {
  // Get email filters from context
  const {
    rowSelectionModel,
    setRowSelectionModel,
    sendMailDisclosure,
    patientEmailList,
    pagination,
    setPagination,
    patientEmailListLoading,
    patientEmailListRefetching,
    refetchUnreadCount,
  } = useOutletContext<CommunicationContext>();

  const [patient] = useCurrentUser();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const { mutate: markAsRead } = usePatientPortalEmailMarkAsRead();
  const { mutateAsync: archiveEmail } = usePatientPortalEmailArchive();
  const { mutateAsync: sendEmail } = usePatientEmailSend();
  const deleteEmailDisclosure = useDisclosure(false);
  const viewEmailDisclosure = useDisclosure(false);
  const [isReplyMode, setIsReplyMode] = useState(false);

  const handleViewEmail = (row: any) => {
    setIsReplyMode(false);
    viewEmailDisclosure.open(row);
    if (!row?.read) {
      markAsRead(
        { emailUuid: row?.uuid },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["patient-portal-emails"],
            });
            refetchUnreadCount();
          },
        }
      );
    }
  };

  const handleReplyEmail = (row: any) => {
    setIsReplyMode(true);
    viewEmailDisclosure.open(row);
    if (!row?.read) {
      markAsRead(
        { emailUuid: row?.uuid },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["patient-portal-emails"],
            });
            refetchUnreadCount();
          },
        }
      );
    }
  };

  const handleSendReply = (replyBody: string, emailData: any) => {
    sendEmail(
      {
        fromEmail: patient?.email || "",
        toEmail: emailData?.senderEmail || "",
        subject: `Re: ${emailData?.subject || ""}`,
        description: replyBody,
      },
      {
        onSuccess: (res) => {
          showSnackbar(getSuccessSnackbar(res));
          viewEmailDisclosure.close();
          queryClient.invalidateQueries({
            queryKey: ["patient-portal-emails"],
          });
        },
      }
    );
  };

  const handleDeleteEmail = (emailUuid: string) => {
    deleteEmailDisclosure.open(emailUuid);
  };

  async function archiveHandler(archiveStatus: boolean, id: any) {
    return archiveEmail({
      emailUuids: [id as string],
      archiveStatus,
    }).then((res) => {
      queryClient.invalidateQueries({ queryKey: ["patient-portal-emails"] });
      refetchUnreadCount();
      showSnackbar(getSuccessSnackbar(res));
      return res;
    });
  }

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "id",
      headerName: "Sr.No",
      maxWidth: 100,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    // {
    //   field: "senderEmail",
    //   flex: 0.5,
    //   minWidth: 180,
    //   disableColumnMenu: true,
    //   headerName: "Sender Email",
    //   renderCell: ({ value }) => (
    //     <RenderWithTooltip value={value || "-"} />
    //   ),
    // },
    {
      field: "subject",
      flex: 0.6,
      minWidth: 220,
      disableColumnMenu: true,
      headerName: "Subject",
      renderCell: ({ row }) => (
        <Text fontWeight={row?.read ? "normal" : "bold"}>
          {row?.subject || "-"}
        </Text>
      ),
    },
    {
      field: "sentBy",
      flex: 0.3,
      minWidth: 150,
      disableColumnMenu: true,
      headerName: "Received From",
      renderCell: ({ value }) => <RenderWithTooltip value={value || "-"} />,
    },
    // {
    //   field: "emailType",
    //   flex: 0.3,
    //   minWidth: 120,
    //   disableColumnMenu: true,
    //   headerName: "Type",
    //   renderCell: ({ value }) => <Text>{value || "-"}</Text>,
    // },
    {
      field: "sendDate",
      flex: 0.2,
      minWidth: 150,
      headerName: "Date & Time",
      renderCell: ({ value }) => (value ? formatDateTime(value) : "-"),
    },
    {
      field: "Action",
      width: 90,
      hideSortIcons: true,
      renderCell: ({ row }) => {
        return (
          <ActionMenu
            options={[
              {
                value: "view",
                label: "View",
              },
              {
                value: "reply",
                label: "Reply",
                isDisabled: true,
                hoverMsg: "Replay is disabled for now.",
              },
              { value: "delete", label: "Delete" },
            ]}
            onActionClick={(v) => {
              if (v === "view") {
                handleViewEmail(row);
              } else if (v === "reply") {
                handleReplyEmail(row);
              } else if (v === "delete") {
                handleDeleteEmail(row?.uuid);
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <Flex flexDirection={"column"} width={"100%"}>
      <PatientSendBulkEmailModal
        disClosureHost={sendMailDisclosure}
        onSave={() => setRowSelectionModel([])}
      />
      <ViewEmail
        disclosureHost={viewEmailDisclosure}
        title={isReplyMode ? "Reply Email" : "View Email"}
        showReply={isReplyMode}
        onSendReply={handleSendReply}
      />
      <DeleteModal
        w="380px"
        open={deleteEmailDisclosure.opened}
        onClose={deleteEmailDisclosure.close}
        id={deleteEmailDisclosure.data}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Email"}
      />

      <GlobalTableHeightWrapper height={COMMUNICATION_LAYOUT_HEIGHT}>
        <CustomDataGridV2
          rows={patientEmailList?.data?.content ?? []}
          getRowId={(row) => row?.uuid}
          rowCount={patientEmailList?.data?.page?.totalElements}
          loading={patientEmailListLoading || patientEmailListRefetching}
          checkboxSelection
          disableRowSelectionOnClick
          columns={COLUMNS}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
        />
      </GlobalTableHeightWrapper>
    </Flex>
  );
};

export default PatientPortalEmailTable;
